import { Modal, Text } from "@mantine/core";
import { FC, useState, useEffect, useRef, useMemo } from "react";
import { Button, color } from "@funded-here-interface/common";
import { useStyle } from "./UploadInvoice.styles";
import useGetSku from "../../hooks/useGetSku";
import useUploadSku from "../../hooks/useUploadSKU";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useDisclosure } from "@mantine/hooks";
import DistributorLayout from "../../layouts/DistributorLayout/DistributorLayout";
import { MantineReactTable, MRT_ColumnDef } from "mantine-react-table";

interface ApiResponse {
  skuId: string;
  orgName: string;
  upc: string;
  description: string;
  poNumber: string;
}

const UploadInvoice: FC = () => {
  const { classes } = useStyle();
  const [isOpenUploadPoModal, setIsOpenUploadPoModal] = useState(false);
  const [isPoUploaded, setIsPoUploaded] = useState(false);
  const [selectedPOFile, setselectedPOFile] = useState<File | null>(null);
  const [skuData, setSkuData] = useState<ApiResponse[]>([]);
  const [selectedSKUId, setSelectedSKUId] = useState<string[]>([]);
  const useGetSKUmutation = useGetSku();
  const uploadSkuMutation = useUploadSku();
  const { token, agreedToMembershipAgreement } = useSelector(
    (state: RootState) => state.auth
  );
  const [opened, { close }] = useDisclosure(
    agreedToMembershipAgreement !== true
  );

  const handleCloseUploadPoModal = () => {
    setIsOpenUploadPoModal(false);
  };

  const fetchData = async () => {
    useGetSKUmutation.mutate(
      { token },
      {
        onSuccess: (data) => {
          setSkuData(data.data);
        },
        onError: (error: any) => {
          toast.error(error);
        },
      }
    );
  };

  const handleSubmit = async () => {
    uploadSkuMutation.mutate(
      { selectedSKUId: selectedSKUId, selectedPOFile: selectedPOFile, token },
      {
        onSuccess: () => {
          setSelectedSKUId([]);
          setIsOpenUploadPoModal(false);
          setIsPoUploaded(false);
          toast.success("Submitted successfully");
          fetchData();
        },
        onError: (error) => {
          toast.error((error as Error).message);
        },
      }
    );
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (
      event &&
      event.target &&
      event.target.files &&
      event.target.files.length > 0
    ) {
      const file = event.target.files[0];
      if (file) {
        setselectedPOFile(file);
        setIsPoUploaded(true);
      }
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const hiddenFileInput = useRef<HTMLInputElement>(null);

  const handleUploadPo = () => {
    if (hiddenFileInput.current) {
      hiddenFileInput.current.click();
    }
  };

  const handleSelectSKU = () => {
    if (selectedSKUId.length === 0) {
      toast.warn("Please select at least one SKU to upload invoice");
      return;
    }
    setIsOpenUploadPoModal(true);
  };

  const columns = useMemo<MRT_ColumnDef<ApiResponse>[]>(
    () => [
      {
        accessorKey: "skuId",
        header: "SKU ID",
      },
      {
        accessorKey: "upc",
        header: "UPC",
      },
      {
        accessorKey: "description",
        header: "Description",
      },
      {
        accessorKey: "poNumber",
        header: "Purchase Order Number",
        Cell: ({ cell }) => <p>{cell.getValue<number>() || "-"}</p>,
      },
      {
        accessorKey: "orgName",
        header: "Merchant",
      },
    ],
    []
  );

  const groupedSkus = useMemo(() => {
    const grouped: { [poNumber: string]: string[] } = {};
    selectedSKUId.forEach((skuId) => {
      const sku = skuData.find((item) => item.skuId === skuId);
      if (sku) {
        const poNumber = sku.poNumber || "Unknown";
        if (!grouped[poNumber]) {
          grouped[poNumber] = [];
        }
        grouped[poNumber].push(sku.skuId);
      }
    });
    return grouped;
  }, [selectedSKUId, skuData]);

  return (
    <DistributorLayout
      step={0}
      content={
        <div className={classes.stepContent}>
          <Text className={classes.headerText} style={{ marginTop: "10px" }}>
            <strong>Upload Invoice</strong>
          </Text>

          <Text className={classes.subHeaderText} style={{ marginTop: "10px" }}>
            Please select the SKU(s) and upload the corresponding invoice(s).
          </Text>

          <MantineReactTable
            columns={columns}
            data={skuData}
            enableRowSelection
            enableSelectAll
            enableColumnActions={false}
            getRowId={(row: ApiResponse) => row.skuId}
            onRowSelectionChange={(updaterOrValue) => {
              const rowSelection =
                typeof updaterOrValue === "function"
                  ? updaterOrValue(
                      selectedSKUId.reduce(
                        (accumulator, id) => ({ ...accumulator, [id]: true }),
                        {} as Record<string, boolean>
                      )
                    )
                  : updaterOrValue;
              const selectedIds = Object.keys(rowSelection);
              setSelectedSKUId(selectedIds);
            }}
            state={{
              rowSelection: selectedSKUId.reduce(
                (accumulator, id) => ({ ...accumulator, [id]: true }),
                {}
              ),
            }}
            mantineTableProps={{
              striped: true,
              highlightOnHover: true,
              withBorder: false,
            }}
          />

          <ToastContainer />

          <div className={classes.btnContainer}>
            <Button
              onClick={handleSelectSKU}
              backgroundColor={color.FHGREEN}
              textColor={color.WHITE}
              children="Upload"
              border={`1px solid ${color.FHGREEN}`}
              width="30%"
            />
          </div>

          <Modal
            opened={isOpenUploadPoModal}
            onClose={handleCloseUploadPoModal}
            title={<strong>Upload Invoice</strong>}
            size="45%"
          >
            <Text>
              Please upload an invoice in PDF for the selected SKU(s).
            </Text>
            {Object.entries(groupedSkus).map(([poNumber, skuIds]) => (
              <div key={poNumber} style={{ marginTop: "16px" }}>
                <Text>
                  Selected Purchase Order Number: {<strong>{poNumber}</strong>}
                </Text>
                <Text style={{ marginTop: "4px" }}>SKU ID(s):</Text>
                {skuIds.map((skuId) => (
                  <Text key={skuId} style={{ marginLeft: "16px" }}>
                    {skuId}
                  </Text>
                ))}
              </div>
            ))}
            <div
              className={classes.buttonWrapper}
              style={{ marginTop: "16px" }}
            >
              {isPoUploaded === false ? (
                <>
                  <Button
                    onClick={() => handleUploadPo()}
                    backgroundColor={color.FHGREEN}
                    textColor={color.WHITE}
                    children="Upload Invoice"
                    border={`1px solid ${color.FHGREEN}`}
                    width="150%"
                  />
                  <input
                    type="file"
                    accept="application/pdf"
                    ref={hiddenFileInput}
                    onChange={handleFileChange}
                    style={{ display: "none" }}
                  />
                </>
              ) : (
                <Button
                  onClick={() => handleSubmit()}
                  backgroundColor={color.FHGREEN}
                  textColor={color.WHITE}
                  children="Submit"
                  border={`1px solid ${color.FHGREEN}`}
                  width="150%"
                />
              )}
            </div>
          </Modal>
        </div>
      }
    />
  );
};

export default UploadInvoice;
